/**
 * Directions Service — OSRM first, then Google Maps JavaScript `DirectionsService`.
 *
 * - **routePreference**: `'fastest'` (default) vs `'shortest'` via OSRM `alternatives=true`
 *   and Google `provideRouteAlternatives`, picking the best candidate.
 * - **trafficAware**: Google `duration_in_traffic` when available; OSRM path + Google ETA merge when both apply.
 * - **retryAttempts**: transient network failures retry with backoff (keeps prior polyline in UI).
 */

/**
 * Decode an encoded polyline string into an array of { lat, lng } objects.
 * @param {string} encoded - The encoded polyline string
 * @param {number} precision - 5 for Google/OSRM polyline, 6 for polyline6
 * @returns {{ lat: number, lng: number }[]}
 */
function decodePolyline(encoded, precision = 5) {
  const factor = Math.pow(10, precision);
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let shift = 0;
    let result = 0;
    let byte;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);

    shift = 0;
    result = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);

    points.push({ lat: lat / factor, lng: lng / factor });
  }

  return points;
}

function pickBestOsrmRoute(routes, preferShortestDistance) {
  if (!routes?.length) return null;
  let best = routes[0];
  routes.forEach((r) => {
    if (preferShortestDistance) {
      if (r.distance < best.distance) best = r;
    } else if (r.duration < best.duration) {
      best = r;
    }
  });
  return best;
}

/**
 * Fetch a route from OSRM server.
 * @param {{ alternatives?: boolean, preferShortestDistance?: boolean }} routeOpts
 */
async function fetchOSRMRoute(osrmUrl, origin, destination, waypoints = [], routeOpts = {}) {
  const alternatives = !!routeOpts.alternatives;
  const preferShortestDistance = !!routeOpts.preferShortestDistance;
  try {
    const allPoints = [origin, ...waypoints, destination];
    const coordString = allPoints.map(p => `${p.lng},${p.lat}`).join(';');
    const altParam = waypoints.length === 0 && alternatives ? '&alternatives=true' : '';
    const url = `${osrmUrl}/route/v1/driving/${coordString}?overview=full&geometries=polyline&steps=false${altParam}`;

    const response = await fetch(url, { signal: AbortSignal.timeout(8000) });

    if (!response.ok) {
      console.error('OSRM HTTP error:', response.status);
      return null;
    }

    const data = await response.json();

    if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
      console.error('OSRM response error:', data.code);
      return null;
    }

    const route = pickBestOsrmRoute(data.routes, preferShortestDistance);
    if (!route) return null;

    const path = decodePolyline(route.geometry, 5);

    if (!path || path.length === 0) {
      console.error('OSRM returned empty path');
      return null;
    }

    return {
      path,
      duration: Math.round(route.duration),
      distance: Math.round(route.distance),
      source: 'osrm',
    };
  } catch (error) {
    if (error.name === 'TimeoutError' || error.name === 'AbortError') {
      console.error('OSRM request timed out');
    } else {
      console.error('OSRM request failed:', error.message);
    }
    return null;
  }
}

/**
 * Sum durations from Directions legs; prefers duration_in_traffic when present and requested.
 */
function sumLegSeconds(legs, preferTraffic) {
  let total = 0;
  let usedTraffic = false;
  (legs || []).forEach((leg) => {
    const inTraffic = leg.duration_in_traffic?.value;
    const base = leg.duration?.value ?? 0;
    if (preferTraffic && inTraffic != null && inTraffic > 0) {
      total += inTraffic;
      usedTraffic = true;
    } else {
      total += base;
    }
  });
  return { seconds: Math.round(total), usedTraffic };
}

function sumLegDistanceMeters(legs) {
  let d = 0;
  (legs || []).forEach((leg) => {
    d += leg.distance?.value || 0;
  });
  return Math.round(d);
}

/**
 * Pick best route from Google Directions result (multiple alternatives).
 */
function pickBestGoogleRoute(result, preferShortestDistance, trafficAware) {
  const routes = result?.routes;
  if (!routes?.length) return null;
  let best = routes[0];
  let bestScore = preferShortestDistance
    ? sumLegDistanceMeters(best.legs)
    : sumLegSeconds(best.legs, trafficAware).seconds;

  routes.forEach((route) => {
    const d = sumLegDistanceMeters(route.legs);
    const t = sumLegSeconds(route.legs, trafficAware).seconds;
    if (preferShortestDistance) {
      if (d < bestScore) {
        best = route;
        bestScore = d;
      }
    } else if (t < bestScore) {
      best = route;
      bestScore = t;
    }
  });
  return best;
}

/**
 * Driving route via Maps JavaScript API (browser). Single leg only.
 */
function fetchGoogleMapsRoute(origin, destination, options = {}) {
  const trafficAware = !!options.trafficAware;
  const preferShortestDistance = !!options.preferShortestDistance;
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.google?.maps?.DirectionsService) {
      resolve(null);
      return;
    }
    const svc = new window.google.maps.DirectionsService();
    const request = {
      origin,
      destination,
      travelMode: window.google.maps.TravelMode.DRIVING,
      provideRouteAlternatives: true,
    };
    const TM = window.google.maps.TrafficModel;
    if (trafficAware && TM) {
      request.drivingOptions = {
        departureTime: new Date(),
        trafficModel: TM.BEST_GUESS,
      };
    }
    svc.route(request, (result, status) => {
      if (status !== window.google.maps.DirectionsStatus.OK || !result?.routes?.[0]) {
        if (status !== window.google.maps.DirectionsStatus.OK) {
          console.warn('Google Directions:', status);
        }
        resolve(null);
        return;
      }
      const route = pickBestGoogleRoute(result, preferShortestDistance, trafficAware);
      if (!route) {
        resolve(null);
        return;
      }
      const overviewPath = route.overview_path || [];
      const path = overviewPath.map((ll) => ({
        lat: typeof ll.lat === 'function' ? ll.lat() : ll.lat,
        lng: typeof ll.lng === 'function' ? ll.lng() : ll.lng,
      }));
      if (!path.length) {
        resolve(null);
        return;
      }
      const distance = sumLegDistanceMeters(route.legs);
      const baseSum = sumLegSeconds(route.legs, false);
      const trafficSum = sumLegSeconds(route.legs, trafficAware);
      const duration = trafficSum.seconds;
      resolve({
        path,
        duration,
        distance,
        source: 'google',
        usedTrafficDuration: trafficAware && trafficSum.usedTraffic,
        durationBaselineSeconds: baseSum.seconds,
      });
    });
  });
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Normalize route preference from fleet / UI.
 * @param {string} [raw]
 * @returns {'fastest'|'shortest'}
 */
function normalizeRoutePreference(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'shortest' || s === 'distance') return 'shortest';
  return 'fastest';
}

/**
 * Get driving directions: OSRM when configured (and for multi-waypoint legs), else Google.
 *
 * @param {Object} options
 * @param {'fastest'|'shortest'} [options.routePreference='fastest']
 * @param {number} [options.retryAttempts=1] — total attempts with backoff on null result
 */
async function getDirections({
  origin,
  destination,
  waypoints = [],
  osrmUrl,
  trafficAware = false,
  routePreference = 'fastest',
  retryAttempts = 1,
}) {
  if (!origin || !destination ||
      isNaN(origin.lat) || isNaN(origin.lng) ||
      isNaN(destination.lat) || isNaN(destination.lng)) {
    return null;
  }

  const pref = normalizeRoutePreference(routePreference);
  const preferShortestDistance = pref === 'shortest';
  const attempts = Math.max(1, Math.min(5, Number(retryAttempts) || 1));

  const runOnce = async () => {
    if (waypoints.length > 0) {
      if (!osrmUrl) return null;
      return fetchOSRMRoute(osrmUrl, origin, destination, waypoints, {
        alternatives: preferShortestDistance,
        preferShortestDistance,
      });
    }

    if (osrmUrl) {
      const osrmResult = await fetchOSRMRoute(osrmUrl, origin, destination, [], {
        alternatives: true,
        preferShortestDistance,
      });
      if (osrmResult && osrmResult.path.length > 0) {
        if (
          trafficAware &&
          typeof window !== 'undefined' &&
          window.google?.maps?.DirectionsService
        ) {
          const googleEta = await fetchGoogleMapsRoute(origin, destination, {
            trafficAware: true,
            preferShortestDistance,
          });
          if (googleEta && typeof googleEta.duration === 'number') {
            return {
              ...osrmResult,
              duration: googleEta.duration,
              durationBaselineSeconds: osrmResult.duration,
              usedTrafficDuration: !!googleEta.usedTrafficDuration,
              source: 'osrm+google-traffic',
              routePreference: pref,
            };
          }
        }
        return { ...osrmResult, routePreference: pref };
      }
    }

    const googleResult = await fetchGoogleMapsRoute(origin, destination, {
      trafficAware,
      preferShortestDistance,
    });
    if (googleResult && googleResult.path.length > 0) {
      return { ...googleResult, routePreference: pref };
    }
    return null;
  };

  let last = null;
  for (let i = 0; i < attempts; i++) {
    last = await runOnce();
    if (last?.path?.length) return last;
    if (i < attempts - 1) await delay(1200 * (i + 1));
  }
  return last;
}

export {
  getDirections,
  decodePolyline,
  fetchOSRMRoute,
  fetchGoogleMapsRoute,
  normalizeRoutePreference,
};
